import React, { useEffect, useState } from "react";
import { HandCoins, Users } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { EmptyState } from "../shared/EmptyState";
import { ErrorState } from "../shared/ErrorState";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatCurrency, todayLocal } from "../../lib/utils";
import { useTaxConfig, computeGst } from "../../lib/tax";
import { useListState } from "../../hooks/useListState";

export function CashCollectionTab() {
  const taxCfg = useTaxConfig();
  const [date, setDate] = useListState("dashboard.cashCollection.date", todayLocal());
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = () => {
    setState({ loading: true, error: null, data: null });
    apiClient.get("/reports/cash-collection", { params: { date } })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load the cash collection report."), data: null }));
  };

  useEffect(load, [date]);

  const r = state.data;

  return (
    <div className="mt-4 space-y-4" data-testid="cash-collection-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="cash-collection-date">Collection date</Label>
          <Input
            id="cash-collection-date" type="date" value={date}
            onChange={(e) => setDate(e.target.value)} className="w-48"
            data-testid="cash-collection-date-input"
          />
        </div>
        {!state.loading && !state.error && r && (
          <div className="flex gap-3">
            <Card data-testid="cash-collection-grand-total-card">
              <CardContent className="flex items-center gap-3 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent-hover">
                  <HandCoins size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Total Collected</p>
                  <p className="font-heading text-xl font-bold text-text-main" data-testid="cash-collection-grand-total">{formatCurrency(r.grand_total)}</p>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="cash-collection-driver-count-card">
              <CardContent className="flex items-center gap-3 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Users size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Drivers · Stops</p>
                  <p className="font-heading text-xl font-bold text-text-main">{r.driver_count} · {r.stop_count}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {state.error ? (
        <ErrorState message={state.error} onRetry={load} testId="cash-collection-error" />
      ) : state.loading ? (
        <Skeleton className="h-64 w-full" />
      ) : r.drivers.length === 0 ? (
        <EmptyState
          title="No cash collected on this day"
          description="Pick another date, or check back after drivers finish their routes."
          testId="cash-collection-empty"
        />
      ) : (
        <div className="space-y-4">
          {r.drivers.map((g) => (
            <Card key={g.driver_id ?? "unknown"} data-testid={`cash-collection-driver-${g.driver_id}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <p className="font-heading text-sm font-semibold text-text-main">
                    {g.driver_name || `Driver #${g.driver_id ?? "—"}`}
                    <span className="ml-2 text-xs font-normal text-text-muted">{g.stop_count} collection{g.stop_count === 1 ? "" : "s"}</span>
                  </p>
                  <p className="font-heading text-base font-bold text-accent-hover" data-testid={`cash-collection-driver-total-${g.driver_id}`}>
                    {formatCurrency(g.total_collected)}
                  </p>
                </div>
                <Table data-testid={`cash-collection-table-${g.driver_id}`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Tiffin No</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Price (incl. GST)</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.stops.map((s) => {
                      const bd = computeGst(s.price, taxCfg);
                      return (
                      <TableRow key={s.payment_id} data-testid={`cash-collection-row-${s.payment_id}`}>
                        <TableCell className="font-medium text-text-main">{s.customer_name || `Sub #${s.subscription_id}`}</TableCell>
                        <TableCell>{s.tiffin_no != null ? `#${s.tiffin_no}` : "—"}</TableCell>
                        <TableCell>{s.route_id != null ? (s.route_name || `Route #${s.route_id}`) : "—"}</TableCell>
                        <TableCell>{s.plan_name || "—"}</TableCell>
                        <TableCell className="text-right text-text-muted">
                          {formatCurrency(bd.total)}
                          {bd.enabled && <div className="text-xs">incl. {formatCurrency(bd.gst)} GST</div>}
                        </TableCell>
                        <TableCell className="text-right font-medium text-text-main">{formatCurrency(s.collected_amount)}</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
